import { lazy, Suspense, useEffect } from 'react'
import { useController, useFormContext } from 'react-hook-form'

import { DefaultElementProps } from 'common/interfaces'
import MapTextField from 'components/MapTextField'
import { SizingContainer } from 'components/SizingContainer'
import { validationsToMapRules } from 'utils/validationsToRules'

import { setAddressList, toggleMapSlideVisibility } from './actions'
import { MapSelectProvider, SelectedMarkerI, useMapSelectDispatch } from './context'
import { MapSelect as MapSelectProps } from './interface'

const MapSelectSlide = lazy(async () => {
  const result = await import('./components/MapSelectSlide')
  return result
})

function MapSelectInner({
  name,
  size,
  validations,
  placeholder = 'Street, City, Address, Zip',
  onChange,
  onFocus,
  label,
  points,
}: MapSelectProps & DefaultElementProps): JSX.Element {
  const dispatch = useMapSelectDispatch()

  const { control } = useFormContext()
  const {
    field,
    fieldState: { error },
  } = useController({
    name,
    control,
    defaultValue: {},
    rules: validationsToMapRules(validations),
  })

  const handleChange = (newValue: SelectedMarkerI): void => {
    field.onChange(newValue)
    onChange?.({ value: newValue })
    dispatch(toggleMapSlideVisibility())
  }

  const openEditModeHandler = (): void => dispatch(toggleMapSlideVisibility())

  const inputValue = (field.value as SelectedMarkerI)?.address ?? ''

  useEffect(() => {
    if (points != null) {
      dispatch(setAddressList(points))
    }
  }, [points])

  useEffect(() => {
    return () => {
      dispatch(toggleMapSlideVisibility())
    }
  }, [])

  return (
    <SizingContainer className="map-select-wrap" size={size}>
      <MapTextField
        field={field}
        inputValue={inputValue}
        error={error}
        placeholder={placeholder}
        label={label}
        openEditMode={openEditModeHandler}
        onFocus={onFocus}
        required={validations?.required?.value}
      />
      <Suspense>
        <MapSelectSlide handleChange={handleChange} />
      </Suspense>
    </SizingContainer>
  )
}

export default function MapSelect(props: MapSelectProps & DefaultElementProps): JSX.Element {
  const { onInteraction, loading, pointButtonText = 'Select' } = props

  return (
    <MapSelectProvider
      loading={loading}
      onInteraction={onInteraction}
      pointButtonText={pointButtonText}
    >
      <MapSelectInner {...props} />
    </MapSelectProvider>
  )
}
